import { useState, useMemo, useRef } from "react";
import { isFieldQuestionVisible } from "../shared/informationRequest/fieldVisibility";
import { isDateInThePastValid, isDateValid, isEmailValid, isTextValid, isYearValid } from "../shared/utils/validation";
import { formatPenceNumberString, isMoneyValid, normaliseMoney } from "../shared/utils/moneyUtils";
import { isPhoneNumberValid } from "../shared/utils/numberUtils";

const dateValidator = (value) => {
  if (value == null || typeof value !== "object") return true;
  return !isDateValid(value.day, value.month, value.year);
};

const dateFieldValidators = {
  date: dateValidator,
  must_happen_by_date: dateValidator,
  date_in_the_past: (value) => !isDateInThePastValid(value),
};

const dateFieldTypes = Object.keys(dateFieldValidators);

const isDateComplete = (answer) => !!(answer?.day && answer?.month && answer?.year);

const field_type_validators = {
  integer: (value) => !/^-?\d+$/.test(String(value).trim()),
  year: (value) => {
    return !isYearValid(value);
  },
  text: (value) => {
    return !isTextValid(value);
  },
  text_area: (value) => {
    return !isTextValid(value);
  },
  postcode: (value) => {
    return !isTextValid(value);
  },
  country: (value) => {
    return !isTextValid(value);
  },
  money: (value) => !isMoneyValid(value),
  email_address: (value) => !isEmailValid(value),
  phone_number: (value) => !isPhoneNumberValid(value),
  mobile_number: (value) => !isPhoneNumberValid(value),
  ...dateFieldValidators,
};

const parseExistingValue = (fieldType, value) => {
  if (dateFieldTypes.includes(fieldType)) {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return null;
    }
    return {
      day: date.getDate(),
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    };
  }

  switch (fieldType) {
    case "boolean":
      return value === "true";
    case "confirmation":
      // The checkbox only shows as ticked for the boolean true, not the
      // string a submitted or drafted value round-trips as.
      return value === "true" ? true : null;
    case "money":
      return formatPenceNumberString(value);
    default:
      return value;
  }
};

const isAnswerBlank = (fieldType, answer) =>
  answer == null ||
  (typeof answer === "string" && answer.trim() === "") ||
  (dateFieldTypes.includes(fieldType) &&
    typeof answer === "object" &&
    Object.values(answer).every((part) => part == null || part === ""));

const normaliseFormSubmissionValue = (value, fieldType) => {
  if (dateFieldTypes.includes(fieldType)) {
    if (isAnswerBlank(fieldType, value)) {
      return value == null ? null : "";
    }
    // parse into a Date to ensure the result is properly padded etc.
    const date = new Date(
      // Ensure it's interpreted as UTC because toISOString converts to
      // UTC and we don't want any rounding issues to shift the day by one.
      Date.UTC(parseInt(value.year), parseInt(value.month) - 1, parseInt(value.day)),
    );
    return date.toISOString().split("T")[0]; // We only want the date portion
  }

  switch (fieldType) {
    case "checkbox":
      return Object.keys(value).filter((key) => value[key]);
    case "money":
      // Pence on the wire, pounds in the box. A cleared answer is sent as "";
      // the staff app skips an empty value rather than unsetting the field.
      if (isAnswerBlank(fieldType, value)) {
        return value == null ? null : "";
      }
      return String(Math.round(parseFloat(normaliseMoney(value)) * 100));
    case "confirmation":
      return value == null ? null : value.toString();
    default:
      if (isAnswerBlank(fieldType, value)) {
        return value == null ? null : "";
      }
      return value.toString();
  }
};

const dateErrorMessage = (fieldType, answer) => {
  if (!isDateComplete(answer)) {
    return null;
  }
  if (dateValidator(answer)) {
    return "The date must be a real date";
  }
  if (fieldType === "date_in_the_past") {
    return "The date must be in the past";
  }
  return null;
};

const validateAnswer = (fieldType, answer, isRequired) => {
  if (isAnswerBlank(fieldType, answer)) {
    return { isValid: !isRequired, errorMessage: null };
  }

  if (fieldType === "checkbox") {
    const hasSelection = Object.values(answer).some(Boolean);
    return { isValid: !isRequired || hasSelection, errorMessage: null };
  }

  const validator = field_type_validators[fieldType];
  if (!validator || !validator(answer)) {
    return { isValid: true, errorMessage: null };
  }

  if (fieldType === "money") {
    return { isValid: false, errorMessage: "Please enter a valid amount" };
  }
  if (fieldType === "integer") {
    return { isValid: false, errorMessage: "Please enter a whole number" };
  }

  const errorMessage = dateFieldTypes.includes(fieldType) ? dateErrorMessage(fieldType, answer) : null;
  return { isValid: false, errorMessage };
};

/**
 * Custom hook for managing dynamic form submission logic.
 * Handles form state, validation, and submission data building for information request forms.
 *
 * @param {Object} doableConfiguration - The doable configuration object containing fields definition
 * @param {Object} [initialDocumentUploads] - Committed uploads to seed each document entry with,
 *   as { field_identifier: [{ upload_id, filename }] } (e.g. restored from a saved draft)
 * @returns {Object} Form state and handlers
 */
const useDynamicFormSubmission = (doableConfiguration, initialDocumentUploads) => {
  const formFields = useMemo(
    () => doableConfiguration.fields.filter((field) => field.field_type !== "document_upload"),
    [doableConfiguration.fields],
  );

  const documentEntries = useMemo(
    () => doableConfiguration.fields.filter((field) => field.field_type === "document_upload"),
    [doableConfiguration.fields],
  );

  // Initialize responses from configuration
  const [responses, setResponses] = useState(() => {
    let initialResponses = {};
    formFields.forEach((field) => {
      let fieldId = field.field_identifier;
      if (field.field_type === "checkbox") {
        initialResponses[fieldId] = {};
        const initialValues = field.values ? field.values : [];
        if (field.choices !== null) {
          field.choices.forEach((choice) => {
            initialResponses[fieldId][choice.value] = initialValues.includes(choice.value);
          });
        }
      } else {
        initialResponses[fieldId] = field.value ? parseExistingValue(field.field_type, field.value) : null;
      }
    });
    return initialResponses;
  });

  const setFieldResponse = (fieldName, newValue) => {
    setResponses((prevResponses) => ({
      ...prevResponses,
      [fieldName]: typeof newValue === "function" ? newValue(prevResponses[fieldName]) : newValue,
    }));
  };

  const [documentUploads, setDocumentUploads] = useState(() =>
    documentEntries.reduce((uploads, entry) => {
      uploads[entry.field_identifier] = initialDocumentUploads?.[entry.field_identifier] ?? [];
      return uploads;
    }, {}),
  );

  const [errors, setErrors] = useState({});
  const [errorMessages, setErrorMessages] = useState({});
  const [reviewing, setReviewing] = useState(false);
  const [fieldToFocus, setFieldToFocus] = useState(null);

  const responsesRef = useRef(responses);
  responsesRef.current = responses;
  // Upload completion and failure callbacks can run in one commit, before a render updates this value.
  const documentUploadFailuresRef = useRef({});

  const fieldRefs = useRef({});

  const setError = (field, error) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: error,
    }));
  };

  const fieldTypes = useMemo(
    () =>
      formFields.reduce((acc, f) => {
        acc[f.field_identifier] = f.field_type;
        return acc;
      }, {}),
    [formFields],
  );

  const fieldChoices = useMemo(
    () =>
      formFields.reduce((acc, f) => {
        acc[f.field_identifier] = f.choices;
        return acc;
      }, {}),
    [formFields],
  );

  const requiredFields = useMemo(
    () =>
      formFields
        .filter(
          (fieldConfig) => fieldConfig.required && isFieldQuestionVisible(doableConfiguration, fieldConfig, responses),
        )
        .map((f) => f.field_identifier),
    [doableConfiguration, formFields, responses],
  );

  const handleInputChange = (field) => (event) => {
    setFieldResponse(field, event);
  };

  const handleUploadsChanged = (identifier) => (uploads) => {
    setDocumentUploads((prevUploads) => ({
      ...prevUploads,
      [identifier]: uploads,
    }));
    if (!documentUploadFailuresRef.current[identifier]) {
      setError(identifier, false);
    }
  };

  const handleUploadFailuresChanged = (identifier) => (hasUploadFailures) => {
    documentUploadFailuresRef.current[identifier] = hasUploadFailures;
    if (!hasUploadFailures) {
      setError(identifier, false);
    }
  };

  const handleCheckboxChange = (field) => (event) => {
    const fieldOption = event.target.value;
    const checked = event.target.checked;

    if (fieldTypes[field] === "confirmation") {
      setFieldResponse(field, checked ? true : null);
    }

    if (fieldTypes[field] === "checkbox") {
      const choices = fieldChoices[field];
      const hasNoneOfTheAbove = choices.some((choice) => choice.is_none_of_the_above === true);

      setFieldResponse(field, (prevValues) => {
        let newValues = { ...prevValues };
        if (hasNoneOfTheAbove) {
          const selectedCheckboxConfig = choices.filter((choice) => choice.value === fieldOption)[0];
          const noneOfTheAboveCheckboxConfig = choices.filter((choice) => choice.is_none_of_the_above === true)[0];

          if (selectedCheckboxConfig.is_none_of_the_above === true) {
            Object.keys(prevValues).forEach((key) => {
              newValues[key] = false;
            });
          } else {
            newValues[noneOfTheAboveCheckboxConfig.value] = false;
          }
        }

        newValues[fieldOption] = checked;
        return newValues;
      });
    }
  };

  const handleDateChange = (field) => (datePart) => (event) => {
    const value = event.target.value;
    setFieldResponse(field, (prevValue) => {
      const date = { ...prevValue } || {};
      date[datePart] = value;
      return date;
    });
  };

  const handleDateFieldValidation = (field) => (datePart) => (value) => {
    const currentDate = { ...(responsesRef.current[field] || {}), [datePart]: value };
    const fieldType = fieldTypes[field];

    // A date the client is still typing is not judged until it is blank or complete,
    // following the GOV.UK validation pattern.
    if (!isAnswerBlank(fieldType, currentDate) && !isDateComplete(currentDate)) {
      return;
    }

    const validation = validateAnswer(fieldType, currentDate, requiredFields.includes(field));
    setError(field, !validation.isValid);
    setErrorMessages((prev) => ({
      ...prev,
      [field]: validation.errorMessage,
    }));
  };

  const handleFieldValidation = (field) => (fieldType) => (event) => {
    if (event.type === "click") {
      setError(field, false);
      return;
    } else if (event.type !== "blur") {
      return;
    }

    // The stored answer, not the input's text: CountryPicker's input holds the typed
    // filter while the response holds the selected code, and blur clears the filter.
    const validation = validateAnswer(fieldType, responsesRef.current[field], requiredFields.includes(field));
    setError(field, !validation.isValid);
    setErrorMessages((prev) => ({ ...prev, [field]: validation.errorMessage }));
  };

  const validateFieldAnswers = () => {
    let isValid = true;
    const newErrors = {};
    const newErrorMessages = {};

    formFields.forEach((fieldConfig) => {
      const question = fieldConfig.field_identifier;
      const isVisible = isFieldQuestionVisible(doableConfiguration, fieldConfig, responses);

      if (!isVisible) {
        newErrors[question] = false;
        newErrorMessages[question] = null;
        return;
      }

      const validation = validateAnswer(fieldConfig.field_type, responses[question], fieldConfig.required);
      newErrors[question] = !validation.isValid;
      newErrorMessages[question] = validation.errorMessage;

      if (!validation.isValid) {
        isValid = false;
      }
    });

    return { errors: newErrors, errorMessages: newErrorMessages, isValid };
  };

  const validateDocumentEntries = () => {
    let isValid = true;
    const newErrors = {};
    const newErrorMessages = {};

    documentEntries.forEach((entry) => {
      const entryVisible = isFieldQuestionVisible(doableConfiguration, entry, responses);
      const uploads = documentUploads[entry.field_identifier] || [];
      const hasUploadFailures = !!documentUploadFailuresRef.current[entry.field_identifier];
      const hasError = entryVisible && (hasUploadFailures || uploads.length === 0);

      // A document entry has no required flag: visible means required, hidden means skipped.
      if (hasError) {
        isValid = false;
      }
      newErrors[entry.field_identifier] = hasError;
      newErrorMessages[entry.field_identifier] =
        entryVisible && hasUploadFailures ? "Remove any file that failed to upload, then try again." : null;
    });

    return { errors: newErrors, errorMessages: newErrorMessages, isValid };
  };

  const handleValidation = () => {
    const fieldValidation = validateFieldAnswers();
    const documentValidation = validateDocumentEntries();

    setErrors({ ...fieldValidation.errors, ...documentValidation.errors });
    setErrorMessages({ ...fieldValidation.errorMessages, ...documentValidation.errorMessages });
    return fieldValidation.isValid && documentValidation.isValid;
  };

  const reviewResponses = (event) => {
    event.preventDefault();
    if (handleValidation()) {
      setReviewing(true);
    }
  };

  const showPrevious = (fieldIdentifier) => {
    if (fieldIdentifier) {
      setFieldToFocus(fieldIdentifier);
    }
    setReviewing(false);
  };

  const focusField = (fieldIdentifier) => {
    const container = fieldRefs.current[fieldIdentifier];
    if (container) {
      // Find first focusable element. A document upload has no input the keyboard can reach:
      // react-dropzone hides its file input and gives it tabIndex -1, putting tabIndex 0 on the
      // drop area instead. So fall back to whatever the entry offers the keyboard.
      const focusableElement =
        container.querySelector("input:not([type='file']), textarea") || container.querySelector("[tabindex='0']");
      if (focusableElement) {
        focusableElement.focus();
      }
    }
    // Clear the focus state after operation completes
    setFieldToFocus(null);
  };

  const buildSubmission = () => {
    const fieldsSubmission = [];
    const fieldsToUnset = [];

    formFields.forEach((field) => {
      const value = responses[field.field_identifier];
      const fieldType = field.field_type;
      const fieldVisible = isFieldQuestionVisible(doableConfiguration, field, responses);

      if (fieldVisible) {
        const normalisedValue = normaliseFormSubmissionValue(value, fieldType);
        if (normalisedValue !== null) {
          if (fieldType === "checkbox") {
            fieldsSubmission.push({
              fieldName: field.field_identifier,
              fieldValues: normalisedValue,
            });
          } else {
            fieldsSubmission.push({
              fieldName: field.field_identifier,
              fieldValue: normalisedValue,
            });
          }
        }
      } else {
        if (!!field.value || !!field.values) {
          fieldsToUnset.push(field.field_identifier);
        }
      }
    });

    const uploadedDocuments = [];

    documentEntries.forEach((entry) => {
      if (!isFieldQuestionVisible(doableConfiguration, entry, responses)) {
        return;
      }

      (documentUploads[entry.field_identifier] || []).forEach((upload) => {
        uploadedDocuments.push({
          identifier: entry.field_identifier,
          upload_id: upload.upload_id,
        });
      });
    });

    return { fieldsSubmission, fieldsToUnset, uploadedDocuments };
  };

  // buildDraft serialises the answers so far for server-side draft storage.
  // Unlike buildSubmission it runs on unvalidated state, so it must never
  // throw: values that don't yet normalise (a partial date, malformed money)
  // are left out until they do. It also skips the visibility filter — like
  // the in-memory state, a draft keeps answers to questions a changed
  // controlling answer has hidden — and omits fields_to_unset, which is
  // recomputed at submission.
  const buildDraft = () => {
    const fields = [];

    formFields.forEach((field) => {
      const value = responses[field.field_identifier];
      if (value == null || value === "") return;

      const fieldType = field.field_type;
      const validator = field_type_validators[fieldType];
      if (validator && validator(value)) return;

      const normalisedValue = normaliseFormSubmissionValue(value, fieldType);
      if (normalisedValue == null) return;

      if (fieldType === "checkbox") {
        fields.push({ fieldName: field.field_identifier, fieldValues: normalisedValue });
      } else {
        fields.push({ fieldName: field.field_identifier, fieldValue: normalisedValue });
      }
    });

    const uploadedDocuments = [];
    documentEntries.forEach((entry) => {
      (documentUploads[entry.field_identifier] || []).forEach((upload) => {
        uploadedDocuments.push({
          identifier: entry.field_identifier,
          filename: upload.filename,
          upload_id: upload.upload_id,
        });
      });
    });

    return { fields, uploaded_documents: uploadedDocuments };
  };

  return {
    responses,
    documentUploads,
    errors,
    errorMessages,
    reviewing,
    setReviewing,
    handleInputChange,
    handleUploadsChanged,
    handleUploadFailuresChanged,
    handleCheckboxChange,
    handleDateChange,
    handleFieldValidation,
    handleDateFieldValidation,
    handleValidation,
    reviewResponses,
    showPrevious,
    buildSubmission,
    buildDraft,
    fieldRefs,
    fieldToFocus,
    focusField,
  };
};

export default useDynamicFormSubmission;
